<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        {{ isEditMode ? 'Back to Manufacturer' : 'Back to Drone Catalog' }}
      </button>
    </div>

    <!-- Page Header -->
    <div class="mb-4">
      <h1 class="h2 mb-1 fw-bold">{{ pageTitle }}</h1>
      <p class="text-muted mb-0">
        {{ isEditMode ? 'Update manufacturer details' : 'Add a new drone manufacturer to the catalog' }}
      </p>
    </div>

    <!-- Loading State -->
    <div *ngIf="loadingManufacturer" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading manufacturer...</p>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Form -->
    <form *ngIf="!loadingManufacturer" [formGroup]="manufacturerForm" (ngSubmit)="onSubmit()">
      <div class="row">
        <!-- Left Column - Main Info -->
        <div class="col-lg-8 mb-4">
          <!-- Basic Information Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-cpu me-2"></i>
                Basic Information
              </h5>
            </div>
            <div class="card-body">
              <!-- Manufacturer Name -->
              <div class="mb-3">
                <label for="name" class="form-label">Manufacturer Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="name"
                  formControlName="name"
                  placeholder="Enter manufacturer name (e.g., DJI, Skydio)"
                  [class.is-invalid]="manufacturerForm.get('name')?.invalid && manufacturerForm.get('name')?.touched"
                />
                <div *ngIf="manufacturerForm.get('name')?.invalid && manufacturerForm.get('name')?.touched" class="invalid-feedback">
                  Manufacturer name is required (minimum 2 characters)
                </div>
              </div>

              <!-- Description -->
              <div class="mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea
                  class="form-control"
                  id="description"
                  formControlName="description"
                  rows="4"
                  placeholder="Brief description of the manufacturer (optional)"
                ></textarea>
              </div>

              <!-- Website URL -->
              <div class="mb-3">
                <label for="url" class="form-label">Website URL</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-link-45deg"></i></span>
                  <input
                    type="url"
                    class="form-control"
                    id="url"
                    formControlName="url"
                    placeholder="https://www.manufacturer.com"
                    [class.is-invalid]="manufacturerForm.get('url')?.invalid && manufacturerForm.get('url')?.touched"
                  />
                </div>
                <div *ngIf="manufacturerForm.get('url')?.invalid && manufacturerForm.get('url')?.touched" class="invalid-feedback d-block">
                  Please enter a valid URL
                </div>
              </div>

              <!-- Logo URL -->
              <div class="mb-0">
                <label for="logoUrl" class="form-label">Logo URL</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-image"></i></span>
                  <input
                    type="url"
                    class="form-control"
                    id="logoUrl"
                    formControlName="logoUrl"
                    placeholder="https://www.manufacturer.com/logo.png"
                    [class.is-invalid]="manufacturerForm.get('logoUrl')?.invalid && manufacturerForm.get('logoUrl')?.touched"
                  />
                </div>
                <div *ngIf="manufacturerForm.get('logoUrl')?.invalid && manufacturerForm.get('logoUrl')?.touched" class="invalid-feedback d-block">
                  Please enter a valid URL
                </div>
                <small class="text-muted">Direct link to the manufacturer's logo image (optional)</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Sidebar -->
        <div class="col-lg-4">
          <!-- Form Actions Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-check2-square me-2"></i>
                Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading || manufacturerForm.invalid"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="bi me-2" [ngClass]="isEditMode ? 'bi-save' : 'bi-plus-lg'"></i>
                  {{ submitButtonText }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  <i class="bi bi-x-lg me-2"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Help Card -->
          <div class="card mt-4">
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i class="bi bi-info-circle fs-4 text-primary"></i>
                <div>
                  <h6 class="fw-semibold mb-1">About Manufacturers</h6>
                  <p class="text-muted mb-0 small">
                    Drone manufacturers are used to organize and categorize drone models in the catalog.
                    After creating a manufacturer, you can add drone models under it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>
